import React from "react";
import Layout, { PageHeader, Surface } from "@/components/Layout";

const Block = ({ children }) => (
  <pre className="bg-zinc-950 text-emerald-200 font-mono text-[11px] p-4 rounded-sm overflow-x-auto">{children}</pre>
);

const SECTIONS = [
  {
    id: "auth",
    title: "Authentication",
    body: (
      <>
        <p className="text-sm text-zinc-600 mb-3">
          Every Proxy API request must include your API key in the <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">X-API-Key</code> header.
          Keys are issued by Blonix Operations and rotated by Super Admin.
        </p>
        <Block>{`X-API-Key: bk_live_xxxxxxxxxxxxxxxxxxxxxxxx
Idempotency-Key: <uuid>   # for POST endpoints`}</Block>
      </>
    ),
  },
  {
    id: "users",
    title: "Users (Cardholders)",
    body: (
        <Block>{`POST   /api/v1/users              { blonix_id }  → issuer addUser (passthrough envelope)
POST   /api/v1/users/query       { blonix_id }  → query cardholder status
GET    /api/v1/users/:blonix_id   → query cardholder status (same as POST /users/query)

# Body for create (only field required):
{
  "blonix_id": "u_3xT9"
}

# blonix_id: your cardholder id (max 32 chars, sent to issuer as thirdId).
# Response: issuer envelope { code, msg, data: { userCode, thirdId }, ok? }`}</Block>
    ),
  },
  {
    id: "cards",
    title: "Cards",
    body: (
      <>
        <p className="text-sm text-zinc-600 mb-3">
          Issue virtual or physical cards, load balance, query issuer bills, and manage card lifecycle.
          Use <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">POST /api/v1/cards/apply</code> for
          the full issuer apply flow (virtual + physical shipping). Legacy <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">POST /api/v1/cards/create</code> remains for sandbox-style issuance.
        </p>
        <Block>{`# List & read
POST   /api/v1/cards                 { blonix_id }  ?page=&limit=  → issuer getCardList (passthrough)
POST   /api/v1/cards/list            { blonix_id }  ?page=&limit=  → same as POST /cards
POST   /api/v1/cards/get              { card_id }  → issuer getCardDetail (passthrough)
POST   /api/v1/cards/transactions     { card_id }  ?page=&limit=  → issuer getBillPage (passthrough)
GET    /api/v1/cards/products?blonix_id=<optional>

# Issue & bind
POST   /api/v1/cards/create        { blonix_id, card_type_id, initial_load? }
POST   /api/v1/cards/apply         { blonix_id, card_type_id, dial_code, phone_number, email,
                                    address, city, country_area, post_code,
                                    first_name?, last_name?, province? }
POST   /api/v1/cards/bind          { blonix_id, active_code, card_number, email,
                                    dial_code, phone_number, address, city, country_area, post_code }

# Lifecycle
POST   /api/v1/cards/freeze        { card_id, reason? }
POST   /api/v1/cards/unfreeze      { card_id }
POST   /api/v1/cards/terminate     { card_id, reason? }
POST   /api/v1/cards/detail        { card_id }
POST   /api/v1/cards/activate      { card_id, active_code }
POST   /api/v1/cards/order         { card_id } | { order_no }

# Balance & sensitive
POST   /api/v1/cards/recharge      { card_id, amount }
POST   /api/v1/cards/recharge/fee  { card_id, amount }
POST   /api/v1/cards/pan-html      { card_id }  → issuer getCardPanHtml (passthrough)
POST   /api/v1/cards/pin           { card_id }  → issuer getCardPin (passthrough)
POST   /api/v1/cards/limits        { card_id }

# Contact & fees
POST   /api/v1/cards/email         { card_id, email }
POST   /api/v1/cards/phone         { card_id, dial_code, phone_number }
POST   /api/v1/cards/fees/query    { card_id }
POST   /api/v1/cards/3ds           { auth_id, approve: true|false }  → respond to 3DS (not simulation)

# Simulated trading (issuer passthrough — test/staging)
POST   /api/v1/trade/authorisation { card_id, bill_amount, transaction_amount?, transaction_currency? }
POST   /api/v1/trade/clearing      { transaction_id, transaction_amount, bill_amount, transaction_currency, card_id? }
POST   /api/v1/trade/refund        { transaction_id, transaction_amount?, bill_amount?, unrelated? }
POST   /api/v1/trade/reversal      { transaction_id, transaction_amount, bill_amount }
POST   /api/v1/trade/auth/3ds      { card_id, transaction_amount, transaction_currency }  → trigger 3DS webhook
POST   /api/v1/trade/auth/card-status { card_id, card_status: SUSPECTED_FRAUD_CVV|EXPIRY|PIN }

# Bills (issuer transaction history)
POST   /api/v1/cards/bills         { card_id, page?, limit?, create_time_start, create_time_end }
GET    /api/v1/cards/bills/feed    ?page=&limit=&search=&card_id=&create_time_start=&create_time_end=
POST   /api/v1/cards/bills/detail  { card_id, order_id }

# Card object
{
  "id": "card_...",
  "blonix_id": "u_3xT9",
  "type": "virtual",
  "status": "active",
  "masked_pan": "**** **** **** 4242",
  "expiry": "07/29",
  "balance": 0.00,
  "currency": "USD"
}`}</Block>
      </>
    ),
  },
  {
    id: "deposits",
    title: "Deposits",
    body: (
      <Block>{`POST   /api/v1/deposits/address    { card_id, currency: "USDC" }
POST   /api/v1/deposits/notify     { card_id, amount, currency, tx_hash }
GET    /api/v1/deposits`}</Block>
    ),
  },
  {
    id: "tx",
    title: "Transactions",
    body: <Block>{`GET    /api/v1/transactions
GET    /api/v1/transactions/:id`}</Block>,
  },
  {
    id: "wh",
    title: "Webhooks",
    body: <Block>{`POST   /api/v1/webhooks/register   { url, events: ["card.*"] }
POST   /api/v1/webhooks/test       { event: "ping" }`}</Block>,
  },
  {
    id: "errors",
    title: "Status codes",
    body: (
      <>
        <p className="text-sm text-zinc-600 mb-3">
          Blonix uses its own <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">88xxxxxx</code> codes for
          platform errors (auth, validation, not found, etc.). Issuer passthrough routes return the issuer&apos;s own
          <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm"> code</code> /{" "}
          <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">msg</code> unchanged on success and business failure.
          Blonix success = <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">88000000</code>.
          Full list: <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">docs/api-status-codes.md</code>
        </p>
        <Block>{`# Blonix success
{ "code": 88000000, "msg": "Success", "data": { ... } }

# Blonix error (HTTP 4xx/5xx)
{ "code": 88300003, "msg": "Insufficient merchant balance", "data": null, "request_id": "..." }

# Issuer passthrough (e.g. POST /cards/list) — issuer codes unchanged, not Blonix 88xxxxxx
{ "code": 0, "msg": "SUCCESS", "data": { "list": [], "total": 0 } }`}</Block>
      </>
    ),
  },
];

export default function ApiDocs() {
  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="api-docs-page">
        <PageHeader
          title="API Reference"
          subtitle="The merchant-facing Proxy API. Upstream issuer internals are abstracted away."
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1 self-start lg:sticky lg:top-6">
            <Surface className="p-4">
              <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">On this page</div>
              <ul className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a className="text-sm text-zinc-600 hover:text-zinc-950 transition-colors duration-150" href={`#${s.id}`}>{s.title}</a>
                  </li>
                ))}
              </ul>
            </Surface>
          </aside>

          <div className="lg:col-span-3 space-y-6">
            {SECTIONS.map((s) => (
              <Surface key={s.id} className="p-6" testId={`api-section-${s.id}`}>
                <h2 id={s.id} className="font-display text-xl tracking-tight font-medium mb-3">{s.title}</h2>
                {s.body}
              </Surface>
            ))}

            <Surface className="p-6">
              <h2 className="font-display text-xl tracking-tight font-medium mb-3">Architecture</h2>
              <p className="text-sm text-zinc-600 mb-3">
                Blonix wraps the underlying card-issuing network behind a versioned Proxy API. Merchants
                see only normalised Blonix DTOs — no upstream IDs, URLs, or credentials are exposed.
              </p>
              <Block>{`Merchant App -> X-API-Key -> Blonix Proxy API
                                |--> Auth + Rate Limit + Idempotency
                                |--> Fee Engine (debit balance)
                                |--> Audit Logger
                                |--> Internal Issuer Adapter
                                       |--> Upstream Card-Issuing Network`}</Block>
            </Surface>
          </div>
        </div>
      </div>
    </Layout>
  );
}
